import { __assign, __awaiter, __generator } from "tslib";
import { createMemo } from 'retil-support';
import { createState, fuse, getSnapshotPromise } from 'retil-source';
import { isRedirect, waitForResponse } from './routerUtils';
import { routeNormalize } from './routers/routeNormalize';
export function createRouter(router, requestService, options) {
    var _this = this;
    if (options === void 0) { options = {}; }
    var _a = options.followRedirects, followRedirects = _a === void 0 ? true : _a, _b = options.maxRedirects, maxRedirects = _b === void 0 ? 5 : _b, _c = options.normalizePathname, normalizePathname = _c === void 0 ? true : _c, initialSnapshot = options.initialSnapshot;
    var normalizedRouter = normalizePathname ? routeNormalize(router) : router;
    var requestSource = requestService[0], requestController = requestService[1];
    var snapshotMemo = createMemo();
    var initialRequest = null;
    var redirectCounter = 0;
    var redirect = function (response, statusOrURL, url) { return __awaiter(_this, void 0, void 0, function () {
        var location;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    location = url || statusOrURL;
                    response.headers.Location = location;
                    response.status = typeof statusOrURL === 'number' ? statusOrURL : 302;
                    if (!followRedirects) return [3 /*break*/, 3];
                    if (++redirectCounter > maxRedirects) {
                        throw new Error('Possible redirect loop detected');
                    }
                    // Navigate in a microtask so that we don't cause any synchronous updates to
                    // components listening to the history.
                    return [4 /*yield*/, Promise.resolve()
                        // Redirects should never be blocked, so we'll force immediate
                        // navigation.
                    ];
                case 1:
                    // Navigate in a microtask so that we don't cause any synchronous updates to
                    // components listening to the history.
                    _a.sent();
                    // Redirects should never be blocked, so we'll force immediate
                    // navigation.
                    return [4 /*yield*/, requestController.navigate(location, {
                            force: true,
                            replace: true,
                        })];
                case 2:
                    // Redirects should never be blocked, so we'll force immediate
                    // navigation.
                    _a.sent();
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var source = fuse(function (use) {
        var request = use(requestSource);
        // If an initial snapshot is provided, use it until a new request is
        // available.
        if (initialSnapshot && (!initialRequest || initialRequest === request)) {
            initialRequest = request;
            return initialSnapshot;
        }
        // There's no guarantee that a fusor function won't be run multiple times
        // for the same request, so let's memoize it.
        var snapshot = snapshotMemo(function () {
            var response = {
                head: [],
                headers: {},
                pendingSuspenses: [],
            };
            response.redirect = redirect.bind(null, response);
            return {
                content: normalizedRouter(request, response),
                response: response,
                request: request,
            };
        }, [request]);
        if (!isRedirect(snapshot.response)) {
            redirectCounter = 0;
        }
        return snapshot;
    });
    var controller = {
        block: requestController.block,
        // We don't wrap `navigate` in an `act()`, as we want in-progress results
        // to be immediately available so they can be handled by suspense.
        navigate: requestController.navigate,
        prefetch: function (action) {
            // TODO: call `requestController.plan`, then create a snapshot using
            // the returned request and add it to cache.
            throw new Error('Unimplemented');
        },
    };
    return [source, controller];
}
export function getInitialSnapshot(router, request, options) {
    if (options === void 0) { options = {}; }
    return __awaiter(this, void 0, void 0, function () {
        var requestSource, requestService, routerSource, snapshot;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    requestSource = createState(request)[0];
                    requestService = [requestSource, {}];
                    routerSource = createRouter(router, requestService, __assign({ followRedirects: false }, options))[0];
                    return [4 /*yield*/, getSnapshotPromise(routerSource)];
                case 1:
                    snapshot = _a.sent();
                    return [4 /*yield*/, waitForResponse(snapshot.response)];
                case 2:
                    _a.sent();
                    return [2 /*return*/, snapshot];
            }
        });
    });
}
